import { StreamClientScrcpy } from '../googDevice/client/StreamClientScrcpy';
import { WsRoomStreamClient } from '../wsRoom/WsRoomStreamClient';
import { resolveRtcRoute } from './streamMode';
import { ControlOnlyClient } from './ControlOnlyClient';

type PhoneLike = {
    id: number;
    instance_name?: string;
    instances: {
        adb_port: number;
        name: string;
        machine: {
            ip: string;
        };
    };
};

type OpenCloudPhoneStreamOptions = {
    phone: PhoneLike;
    showToolBox?: boolean;
    isMasterCloud?: boolean;
    sourceType?: 'single' | 'group_control';
};

export type CloudPhoneStreamClient = StreamClientScrcpy | WsRoomStreamClient;

export async function openCloudPhoneStream(
    options: OpenCloudPhoneStreamOptions,
): Promise<CloudPhoneStreamClient> {
    const { phone } = options;
    const udid = `${phone.instances.machine.ip}:${phone.instances.adb_port}`;

    try {
        const rtcRoute = await resolveRtcRoute(phone.instances.name);
        if (rtcRoute.mode === 'ws_room' && rtcRoute.pullUrl) {
            console.info('[openCloudPhoneStream] using ws_room', {
                phoneId: phone.id,
                vendor: rtcRoute.vendor,
            });
            return WsRoomStreamClient.create({
                udid,
                padCode: phone.instances.name,
                pullUrl: rtcRoute.pullUrl,
                deviceName: phone.instance_name || phone.instances.name || udid,
                show_ToolBox: options.showToolBox,
                is_master_cloud: options.isMasterCloud,
                sourceType: options.sourceType,
                phoneId: phone.id,
            });
        }
        console.info('[openCloudPhoneStream] falling back to ws_scrcpy', {
            phoneId: phone.id,
            vendor: rtcRoute.vendor || 'unknown',
        });
    } catch (error) {
        console.warn('[openCloudPhoneStream] RTC route check failed, fallback to ws_scrcpy', error);
    }

    console.info('[openCloudPhoneStream] using ws_scrcpy', {
        phoneId: phone.id,
        routeMode: 'fallback',
    });
    const wsHost = String(process.env.VUE_APP_API_WS_URL || '').split('/')[0] || '';
    const wsUrl = `wss://${process.env.VUE_APP_API_WS_URL}?action=proxy-adb&remote=tcp:8886&udid=${udid}`;
    const parsedQuery = new URLSearchParams();
    parsedQuery.set('action', 'stream');
    parsedQuery.set('player', 'mse');
    parsedQuery.set('udid', udid);
    parsedQuery.set('hostname', wsHost);
    parsedQuery.set('ws', wsUrl);
    parsedQuery.set('port', '10443');
    parsedQuery.set('secure', 'true');
    return StreamClientScrcpy.start(
        parsedQuery,
        undefined,
        undefined,
        undefined,
        undefined,
        options.showToolBox,
        options.isMasterCloud,
        options.sourceType,
        phone.id,
    );
}

/**
 * 仅建立控制通道（不拉流、不挂载 DOM），用于群控从机。
 * 画面由截图轮询驱动，本通道只负责把 master 下发的控制消息转送给云机。
 * ws_room 模式下向 pullUrl 追加 mode=control_only 查询参数，backend 据此跳过视频转发。
 */
export async function openCloudPhoneControlChannel(phone: PhoneLike): Promise<ControlOnlyClient> {
    const udid = `${phone.instances.machine.ip}:${phone.instances.adb_port}`;
    try {
        const rtcRoute = await resolveRtcRoute(phone.instances.name);
        if (rtcRoute.mode === 'ws_room' && rtcRoute.pullUrl) {
            const controlOnlyUrl = appendControlOnlyParam(rtcRoute.pullUrl);
            console.info('[openCloudPhoneControlChannel] ws_room control-only', {
                phoneId: phone.id,
                pullUrl: controlOnlyUrl,
            });
            return ControlOnlyClient.create({
                mode: 'ws_room',
                udid,
                wsUrl: controlOnlyUrl,
                phoneId: phone.id,
                deviceName: phone.instance_name || phone.instances.name || udid,
            });
        }
    } catch (error) {
        console.warn('[openCloudPhoneControlChannel] RTC route lookup failed, fallback to ws_scrcpy', error);
    }
    const wsUrl = `wss://${process.env.VUE_APP_API_WS_URL}?action=proxy-adb&remote=tcp:8886&udid=${udid}`;
    console.info('[openCloudPhoneControlChannel] ws_scrcpy control-only', { phoneId: phone.id });
    return ControlOnlyClient.create({
        mode: 'ws_scrcpy',
        udid,
        wsUrl,
        phoneId: phone.id,
        deviceName: phone.instance_name || phone.instances.name || udid,
    });
}

function appendControlOnlyParam(pullUrl: string): string {
    const sep = pullUrl.includes('?') ? '&' : '?';
    return `${pullUrl}${sep}mode=control_only`;
}
